<template>
    <div class="dbresult">
        <!-- 错误 -->
        <transition name="dbresult-anim" @enter="onPanelEnter" @after-enter="onPanelAfterEnter"
            @leave="onPanelLeave" @after-leave="onPanelAfterLeave">
            <div v-if="error" key="err" class="dbresult__error">{{ error }}</div>
        </transition>

        <!-- 结果体：执行中【不卸载旧结果】，只有「表 ↔ 非表 ↔ 错误」这种形变才做一次过渡 -->
        <transition name="dbresult-anim" @enter="onPanelEnter" @after-enter="onPanelAfterEnter"
            @leave="onPanelLeave" @after-leave="onPanelAfterLeave">
            <div v-if="!error && result" key="res" class="dbresult__body">
                <div class="dbresult__status">
                    <template v-if="result.type === 'QUERY'">返回 {{ result.rowCount }} 行</template>
                    <template v-else>执行成功，影响 {{ result.affectedRows }} 行</template>
                    <span class="dbresult__sep">·</span>
                    <span>耗时 {{ result.elapsedMs }} ms</span>
                    <template v-if="sql">
                        <span class="dbresult__sep">·</span>
                        <!-- 执行的 SQL：超长省略，悬浮 title 查看完整语句 -->
                        <span class="dbresult__sql" :title="sql">{{ sql }}</span>
                    </template>
                </div>

                <el-alert v-for="(w, i) in (result.warnings || [])" :key="i" :title="w" type="warning"
                    :closable="false" show-icon class="dbresult__warn" />

                <!-- 表格区：表↔非表时收起/展开；表↔表时数据就地替换、不做动画 -->
                <transition name="dbresult-anim" @enter="onPanelEnter" @after-enter="onPanelAfterEnter"
                    @leave="onPanelLeave" @after-leave="onPanelAfterLeave">
                    <div v-if="showTable" key="tbl" class="dbresult__tablewrap">
                        <div class="dbresult__actions">
                            <el-button size="small" type="primary"
                                :disabled="selection.length === 0 || selection.length > maxSelect"
                                @click="emitFavorite">
                                收藏所选行（{{ selection.length }}）
                            </el-button>
                            <span class="dbresult__tips">选中 1~100 行可收藏；需先保存该 SQL 块</span>
                        </div>
                        <div class="dbresult__toolbar">
                            <el-input v-model="keyword" class="dbresult__search" size="small" clearable
                                placeholder="搜索结果（关键字匹配所有列）">
                                <template #prefix>
                                    <el-icon>
                                        <Search />
                                    </el-icon>
                                </template>
                            </el-input>
                            <el-button size="small" @click="openColumnSetting">
                                <el-icon class="el-icon--left">
                                    <Setting />
                                </el-icon>
                                列设置
                            </el-button>
                            <!-- 列的展示格式（仅前端转换展示，不改数据/不影响排序筛选） -->
                            <el-popover v-model:visible="formatPanel" placement="bottom-start" :width="420"
                                trigger="click" popper-class="dbresult-format-popover">
                                <template #reference>
                                    <el-button size="small">
                                        <el-icon class="el-icon--left">
                                            <MagicStick />
                                        </el-icon>
                                        格式
                                    </el-button>
                                </template>
                                <div class="dbresult-format">
                                    <div class="dbresult-format__head">
                                        列的显示格式（只影响展示，不改数据；排序/筛选/编辑仍按原值）<br />
                                        千分位 1,234,567 · 金额 1,234.50 · 百分比 12.34% · 日期时间（时间戳按量级自动识别秒/毫秒/微秒）
                                    </div>
                                    <div v-if="formatDefs.length === 0" class="dbresult-format__empty">
                                        当前结果没有可格式化的数值 / 布尔 / JSON 列
                                    </div>
                                    <div v-for="d in formatDefs" :key="d.field" class="dbresult-format__row">
                                        <span class="dbresult-format__name" :title="d.title">{{ d.title }}</span>
                                        <!-- 用原生 select：下拉不会挂到 body，点选项不会被当成「点外面」而关闭面板 -->
                                        <select class="dbresult-format__select" :value="formatByColumn[d.title] || ''"
                                            @change="e => setColumnFormat(d.title, e.target.value)">
                                            <option v-for="o in formatOptions(d)" :key="o.value" :value="o.value">
                                                {{ o.label }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </el-popover>
                            <span v-if="keyword" class="dbresult__match">匹配 {{ matchedRows }} / {{ totalRows }} 行</span>
                            <span class="dbresult__tips">{{ canEdit ? '双击单元格进入编辑；点击表头排序、列头漏斗筛选；「列设置」可显隐/拖拽排序/冻结列' : '点击表头排序、列头漏斗筛选；「列设置」可显隐/拖拽排序/冻结列' }}</span>
                            <!-- 结果可编辑（简单单表查询 + 写权限）：右上角行级编辑操作 -->
                            <span v-if="canEdit" class="dbresult__editbar">
                                <el-button size="small" @click="addRow">新增行</el-button>
                                <el-button size="small" :disabled="selection.length === 0" @click="deleteSelectedRows">
                                    删除行
                                </el-button>
                                <el-button size="small" type="primary" :disabled="!canSubmit" :loading="savingEdit"
                                    @click="submitChanges">提交</el-button>
                                <el-button size="small" :disabled="!canSubmit" @click="rollbackChanges">回滚</el-button>
                                <span v-if="pendingCount" class="dbresult__editcount">{{ pendingCount }} 处待提交</span>
                            </span>
                            <el-tooltip v-else-if="editReason" :content="editReason" placement="top">
                                <span class="dbresult__editlock">暂不可编辑</span>
                            </el-tooltip>
                        </div>
                        <!-- vxe-table：高度跟分隔区走；行数超过 20 自动启用虚拟滚动 -->
                        <div ref="tableBox" class="dbresult__tablebox">
                        <vxe-table ref="xTable" class="dbresult__table" :data="viewData" :height="tableHeight" size="small"
                            border stripe show-overflow auto-resize
                            :row-config="{ keyField: '__idx', isHover: true }"
                            :column-config="{ resizable: true }"
                            :custom-config="customConfig"
                            :filter-config="{ showIcon: true }"
                            :edit-config="editConfig"
                            :row-class-name="rowClassName"
                            :scroll-y="{ enabled: true, gt: 20 }"
                            :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }"
                            @edit-actived="onEditActived" @edit-closed="onEditClosed"
                            @checkbox-change="onCheckboxChange" @checkbox-all="onCheckboxChange">
                            <vxe-column type="checkbox" width="44" />
                            <vxe-column type="seq" width="52" title="#" />
                            <vxe-column v-for="d in columnDefs" :key="d.field" :field="d.field" :title="d.title"
                                :min-width="d.minWidth" :sortable="true" :sort-by="d.sortBy" :filters="d.filters"
                                :filter-method="filterMethod" :edit-render="columnEditRender(d.col)">
                                <!-- 表头三行：字段名 / 字段类型 / 字段注释（注释按来源表字段匹配，见 colComment） -->
                                <template #header>
                                    <div class="dbresult__head">
                                        <span class="dbresult__head-name">{{ d.col.name }}</span>
                                        <span v-if="colType(d.col)" class="dbresult__head-type">{{ colType(d.col) }}</span>
                                        <span v-if="colComment(d.col)" class="dbresult__head-cmt"
                                            :title="colComment(d.col)">{{ colComment(d.col) }}</span>
                                    </div>
                                </template>
                                <template #default="{ row }">
                                    <!-- 默认纯文本（双击由 vxe 进入编辑，见 editConfig）；改动高亮；数字列可套展示格式 -->
                                    <span v-if="isCellEditable(row, d.ci)" class="dbresult__cell-view"
                                        :class="{ 'is-dirty': isCellDirty(row, d.ci) }"
                                        title="双击编辑">{{ cellText(row, d) }}</span>
                                    <span v-else-if="row['c' + d.ci] === null" class="dbresult__null">NULL</span>
                                    <span v-else :title="cellTitle(row, d)">{{ cellText(row, d) }}</span>
                                </template>
                            </vxe-column>
                        </vxe-table>
                        </div>
                    </div>
                </transition>
            </div>
        </transition>

        <!-- 执行中：保留旧结果，在其上叠加「转圈等待」；首次执行无旧结果，只走正常展开动画 -->
        <transition name="dbresult-fade">
            <div v-if="loading && result && !error" class="dbresult__loading">
                <el-icon class="is-loading" :size="22">
                    <Loading />
                </el-icon>
            </div>
        </transition>
    </div>
</template>

<script>
/**
 * 结果面板
 * <p>拆成独立子组件的目的：让「大结果表格」只在结果变化时重渲染，
 * 避免父级（如 SQL 输入框打字）的每次重渲染都连带重算表格。</p>
 */
import { ElMessage } from 'element-plus';

// 结果列宽：最小值 / 最大值（内容再长也不超过上限；表头底线优先）
const MIN_COL_WIDTH = 88;
const MAX_COL_WIDTH = 420;
// 表头右侧的固定占位：单元格左右内边距(10+10) + 排序图标 + 筛选图标 + 余量
// 图标是 flex 不收缩项，名称必须避开这块空间，否则会被压成省略号
const HEADER_RESERVE = 74;
// 布尔展示格式可识别的取值（统一小写比较）
const BOOL_TRUE = ['1', 'true', 't', 'y', 'yes', '是'];
const BOOL_FALSE = ['0', 'false', 'f', 'n', 'no', '否'];

export default {
    name: 'DbResultPanel',
    props: {
        // 结果对象（父级已 markRaw，避免深层响应式）
        result: { type: Object, default: null },
        error: { type: String, default: '' },
        // 是否正在执行：为 true 时保留旧结果并在其上叠加转圈遮罩
        loading: { type: Boolean, default: false },
        // 本次执行的 SQL（展示在状态行末尾，超长省略、悬浮查看完整）
        sql: { type: String, default: '' },
        // 列元数据：`{ 表名: [{ name, type, comment }] }`（父组件传当前库的列缓存）。
        // 结合结果列自带的 `table`/`column`（后端 JDBC 元数据，已解析别名）匹配字段注释。
        columnMeta: { type: Object, default: null },
        // 当前连接是否可写（owner / 读写团队）——与后端返回的 result.edit 共同决定能否编辑
        canWrite: { type: Boolean, default: false },
        /**
         * 提交改动：由父组件注入（负责生产防护锁确认 + 调接口 + 成功后重查刷新）。
         * 入参 `{ table, schema, changes }`，返回 Promise<boolean>（true=提交成功）。
         */
        commitHandler: { type: Function, default: null },
        maxSelect: { type: Number, default: 100 }
    },
    emits: ['favorite'],
    data() {
        return {
            selection: [],
            keyword: '',
            // 编辑态：结果副本（可改），仅 canEdit 时作为表格数据源
            editRows: [],
            // 待删除的原有行（提交时生成 DELETE）
            deletedRows: [],
            // 是否正处于单元格编辑中（双击出现输入框即置真）→ 提交/回滚按钮点亮
            editActive: false,
            // 提交中
            savingEdit: false,
            // 新增行/键的递增序号（保证 vxe keyField 唯一）
            editSeq: 0,
            // 每列的列头筛选项 / 数值排序（按结果一次性计算，避免单元格编辑时反复遍历全量数据）
            filtersByField: {},
            // 「格式」面板：各列的展示格式（列名 → thousands / money / percent / datetime / seconds / millis / bytes / bool / json）
            formatPanel: false,
            formatByColumn: {},
            // 列类型标记（field → ['number' | 'bool' | 'json']），随结果重建；决定该列能选哪些展示格式
            columnKinds: {},
            // 内容宽度（field → px），随结果/展示格式重建；避免单元格编辑时反复量算
            contentWidths: {},
            // 表格可视高度：跟结果区分隔区走，由 ResizeObserver 写入
            tableHeight: 240
        };
    },
    computed: {
        // 是否展示表格区：仅「查询且有列」时
        showTable() {
            return !!(this.result && this.result.type === 'QUERY' && (this.result.columns || []).length);
        },
        // 后端返回的可编辑元信息
        editMeta() {
            return (this.result && this.result.edit) || null;
        },
        // 是否进入可编辑：写权限 + 后端判定为可编辑 + 有表格
        canEdit() {
            return !!(this.canWrite && this.showTable && this.editMeta && this.editMeta.editable);
        },
        // 不可编辑的原因（用于悬浮提示）
        editReason() {
            return this.editMeta && !this.editMeta.editable ? (this.editMeta.reason || '') : '';
        },
        // 结果原始行（只读副本）；仅在 result 引用变化时重算（result 为 markRaw）
        originTableData() {
            if (!this.result || !this.result.columns) return [];
            return (this.result.rows || []).map((row, ri) => {
                const obj = { __idx: ri };
                row.forEach((cell, ci) => {
                    obj['c' + ci] = cell;
                });
                return obj;
            });
        },
        // 表格数据源：可编辑时用编辑副本，否则用原始行
        tableData() {
            return this.canEdit ? this.editRows : this.originTableData;
        },
        totalRows() {
            return this.tableData.length;
        },
        // 关键字跨所有列过滤（不区分大小写）
        viewData() {
            const key = this.keyword.trim().toLowerCase();
            if (!key) return this.tableData;
            const columns = this.result.columns || [];
            return this.tableData.filter(row => {
                for (let ci = 0; ci < columns.length; ci++) {
                    const cell = row['c' + ci];
                    if (cell !== null && cell !== undefined && String(cell).toLowerCase().includes(key)) {
                        return true;
                    }
                }
                return false;
            });
        },
        matchedRows() {
            return this.viewData.length;
        },
        // 列设置面板（参考 dataview/CardTable.vue）：显隐、拖拽排序、左右冻结、列宽
        customConfig() {
            return {
                trigger: 'manual',
                // 必须用 simple 面板：popup（modal）面板点「冻结」时，vxe 4.6 会先把 column.fixed
                // 改成新值、再调 setColumnFixed(column, column.fixed)，而该方法内部判断「值没变」
                // 会直接跳过刷新 —— 表现为列内容消失、原位置留空列、冻结列不出现。
                // simple 面板走 changeFixedOption → setColumnFixed 的正确路径（点击即刷新）。
                mode: 'simple',
                allowFixed: true,
                allowSort: true,
                // 显隐/排序在关闭面板时统一应用；冻结在点击时即时生效
                immediate: false,
                storage: false
            };
        },
        // 列定义：标题三行 + 自适应列宽 + 列头筛选项 / 数值排序（参考 dataview/CardTable.vue）
        columnDefs() {
            const columns = (this.result && this.result.columns) || [];
            return columns.map((col, ci) => {
                const field = 'c' + ci;
                return Object.assign({
                    col,
                    ci,
                    field,
                    title: col.name || field,
                    minWidth: this.columnWidth(col, field),
                    kinds: this.columnKinds[field] || []
                }, this.filtersByField[field] || {});
            });
        },
        // 可设置展示格式的列：数值 / 布尔 / JSON 列
        formatDefs() {
            return this.columnDefs.filter(d => d.kinds && d.kinds.length);
        },
        // 可编辑列（原始列名，小写集合）
        editableColumnSet() {
            const list = (this.editMeta && this.editMeta.editableColumns) || [];
            return new Set(list.map(c => String(c).toLowerCase()));
        },
        // 主键列（原始列名，小写集合）
        pkColumnSet() {
            const list = (this.editMeta && this.editMeta.pkColumns) || [];
            return new Set(list.map(c => String(c).toLowerCase()));
        },
        // 待提交改动数量（新增 + 修改 + 删除）
        pendingCount() {
            if (!this.canEdit) return 0;
            return this.collectChanges().length;
        },
        // 提交/回滚是否可用：有改动，或正处于单元格编辑中（双击出现输入框即点亮）
        canSubmit() {
            return this.editActive || this.pendingCount > 0;
        },
        // vxe 单元格编辑配置（对象无响应式依赖 → 只创建一次，props 引用稳定）
        editConfig() {
            return {
                trigger: 'dblclick',   // 双击进入编辑（避免误操作）
                mode: 'cell',
                showIcon: false,
                // 返回 false 时该单元格不可编辑（只读列 / 原有行的主键列）
                beforeEditMethod: ({ row, column }) => this.canEditCellField(row, column.field)
            };
        }
    },
    watch: {
        result() {
            // 结果整体替换，清掉搜索词与选中状态，并重建编辑副本
            this.keyword = '';
            this.clearSelection();
            this.resetEditRows();
        },
        keyword() {
            // 过滤后可能隐藏已选行，清空选中避免「看不到却被收藏」
            this.clearSelection();
        },
        showTable(val) {
            if (val) this.$nextTick(() => this.observeTableBox());
            else this.disconnectTableBox();
        }
    },
    mounted() {
        // 首次挂载即初始化编辑副本（后续 result 变化由 watcher 处理）
        this.resetEditRows();
        this.$nextTick(() => this.observeTableBox());
    },
    beforeUnmount() {
        this.disconnectTableBox();
    },
    methods: {
        observeTableBox() {
            this.disconnectTableBox();
            const el = this.$refs.tableBox;
            if (!el || typeof ResizeObserver === 'undefined') return;
            this.tableRo = new ResizeObserver((entries) => {
                const h = Math.floor(entries[0].contentRect.height);
                if (h > 0 && Math.abs(h - this.tableHeight) > 2) this.tableHeight = h;
            });
            this.tableRo.observe(el);
            const start = Math.floor(el.clientHeight);
            if (start > 0) this.tableHeight = start;
        },
        disconnectTableBox() {
            if (this.tableRo) {
                this.tableRo.disconnect();
                this.tableRo = null;
            }
        },
        clearSelection() {
            this.selection = [];
            // 结果/筛选变化后，清掉 vxe-table 内部的行选中状态（keyField 相同可能残留误选）
            this.$nextTick(() => {
                const table = this.$refs.xTable;
                if (table && table.clearCheckboxRow) {
                    table.clearCheckboxRow();
                }
            });
        },
        // vxe-table 的 checkbox-change / checkbox-all 事件都会回传 records
        onCheckboxChange({ records }) {
            this.selection = records || [];
        },
        // 打开 vxe 自带的列设置面板：显隐、拖拽排序、左/右固定，全部由用户自己点
        openColumnSetting() {
            const table = this.$refs.xTable;
            if (table && table.openCustom) {
                table.openCustom();
            }
        },
        // 为某列构建列头筛选项、数值排序，并识别「数值 / 布尔 / JSON」列（供展示格式选择）
        buildColumnFilter(field, rows) {
            const MAX_OPTIONS = 50;
            const values = new Map();
            let overflow = false;
            let numeric = rows.length > 0;
            let bool = rows.length > 0;
            let json = rows.length > 0;
            let hasValue = false;
            let jsonProbe = null;   // 第一个非空值，用来判断该列是否是 JSON（只探测一个样本）
            rows.forEach(row => {
                const raw = row ? row[field] : null;
                if (raw !== null && raw !== undefined && raw !== '') {
                    hasValue = true;
                    if (isNaN(Number(raw))) numeric = false;
                    if (bool) {
                        const v = String(raw).trim().toLowerCase();
                        if (BOOL_TRUE.indexOf(v) < 0 && BOOL_FALSE.indexOf(v) < 0) bool = false;
                    }
                    if (json && jsonProbe === null) jsonProbe = String(raw);
                }
                if (overflow) return;
                const key = raw === null || raw === undefined ? '' : String(raw);
                if (values.has(key)) return;
                if (values.size >= MAX_OPTIONS) {
                    overflow = true;
                    return;
                }
                values.set(key, true);
            });
            numeric = numeric && hasValue;
            bool = bool && hasValue;
            json = json && hasValue && this.isJsonText(jsonProbe);
            const kinds = [];
            if (numeric) kinds.push('number');
            if (bool) kinds.push('bool');
            if (json) kinds.push('json');
            const extra = { kinds };
            if (!overflow && values.size) {
                extra.filters = Array.from(values.keys())
                    .sort((a, b) => a.localeCompare(b, 'zh-CN', { numeric: true }))
                    .map(v => ({ label: v === '' ? '（空）' : v, value: v }));
            }
            if (numeric) {
                // 数值列按数值排序（字符串比较会把 10 排到 2 前面）；空值视为最小
                extra.sortBy = (row) => {
                    const v = row ? row[field] : null;
                    if (v === null || v === undefined || v === '') return Number.NEGATIVE_INFINITY;
                    return Number(v);
                };
            }
            return extra;
        },
        // 文本是否是 JSON 对象/数组（只用于判断该列要不要提供「JSON 美化」）
        isJsonText(text) {
            if (!text) return false;
            const t = String(text).trim();
            if (t.charAt(0) !== '{' && t.charAt(0) !== '[') return false;
            try {
                const value = JSON.parse(t);
                return value !== null && typeof value === 'object';
            } catch (e) {
                return false;
            }
        },
        // 按当前结果一次性重建各列的筛选项 / 列类型标记（结果变化时调用）
        rebuildColumnFilters() {
            const columns = (this.result && this.result.columns) || [];
            const rows = this.tableData;
            const filters = {};
            const kinds = {};
            columns.forEach((col, ci) => {
                const field = 'c' + ci;
                const info = this.buildColumnFilter(field, rows);
                filters[field] = { filters: info.filters, sortBy: info.sortBy };
                kinds[field] = info.kinds;
            });
            this.filtersByField = filters;
            this.columnKinds = kinds;
        },
        // 按当前结果（含展示格式）预先量算每列内容宽度：到上限即停止，大结果也只扫前 2000 行
        rebuildContentWidths() {
            const columns = (this.result && this.result.columns) || [];
            const rows = this.tableData;
            const limit = Math.min(rows.length, 2000);
            const out = {};
            columns.forEach((col, ci) => {
                const field = 'c' + ci;
                const title = col.name || field;
                let maxWidth = 0;
                for (let i = 0; i < limit; i += 1) {
                    const raw = rows[i] ? rows[i][field] : null;
                    if (raw === null || raw === undefined || raw === '') continue;
                    const width = this.textWidth(this.formatValue(raw, title), 7);
                    if (width > maxWidth) {
                        maxWidth = width;
                        // 已到列宽上限，后面的行无需再量
                        if (maxWidth + 20 >= MAX_COL_WIDTH) break;
                    }
                }
                // +20 为单元格左右内边距
                out[field] = Math.ceil(maxWidth + 20);
            });
            this.contentWidths = out;
        },
        // vxe 列头筛选：选中的取值需与单元格文本完全一致（过滤器值均为字符串）
        filterMethod({ value, row, column }) {
            const list = Array.isArray(value) ? value : [value];
            if (!list.length) return true;
            const raw = row ? row[column.field] : null;
            const text = raw === null || raw === undefined ? '' : String(raw);
            return list.some(v => String(v) === text);
        },
        // ---------- 列的展示格式（只影响展示，不改数据） ----------
        // 某列可选的格式：按列类型（数值 / 布尔 / JSON）裁剪，避免给出无意义的选项
        formatOptions(d) {
            const kinds = d.kinds || [];
            const list = [{ label: '原样', value: '' }];
            if (kinds.indexOf('number') >= 0) {
                list.push({ label: '千分位（1,234,567）', value: 'thousands' });
                list.push({ label: '金额（2 位小数）', value: 'money' });
                list.push({ label: '百分比（12.34%）', value: 'percent' });
                list.push({ label: '日期时间（时间戳）', value: 'datetime' });
                list.push({ label: '时长·秒（1天2分3秒）', value: 'seconds' });
                list.push({ label: '时长·毫秒', value: 'millis' });
                list.push({ label: '字节（1.5 MB）', value: 'bytes' });
            }
            if (kinds.indexOf('bool') >= 0) list.push({ label: '是 / 否', value: 'bool' });
            if (kinds.indexOf('json') >= 0) list.push({ label: 'JSON 美化（悬浮看结构）', value: 'json' });
            return list;
        },
        // 单元格展示文本：按列格式转换；原值不动，排序/筛选/编辑仍用原值
        cellText(row, d) {
            const raw = row ? row['c' + d.ci] : null;
            if (raw === null || raw === undefined) return '';
            return this.formatValue(raw, d.title);
        },
        // 按列名套用展示格式（未设置格式 / 非数值时原样返回）
        formatValue(raw, title) {
            const fmt = this.formatByColumn[title];
            if (!fmt) return raw;
            if (fmt === 'bool') return this.formatBool(raw);
            if (fmt === 'json') return this.compactJson(raw);
            const num = Number(raw);
            if (raw === '' || !isFinite(num)) return raw;
            if (fmt === 'seconds') return this.formatDuration(num);
            if (fmt === 'millis') return this.formatDuration(num / 1000, true);
            if (fmt === 'bytes') return this.formatBytes(num);
            if (fmt === 'thousands') return this.formatThousands(raw);
            if (fmt === 'money') return this.formatMoney(num);
            if (fmt === 'percent') return this.formatPercent(num);
            if (fmt === 'datetime') return this.formatTimestamp(num);
            return raw;
        },
        // 套了格式的单元格：悬浮显示原始值，便于核对（JSON 列改为悬浮显示格式化后的结构）
        cellTitle(row, d) {
            const fmt = this.formatByColumn[d.title];
            if (!fmt) return undefined;
            const raw = row ? row['c' + d.ci] : null;
            if (raw === null || raw === undefined) return undefined;
            return fmt === 'json' ? this.formatJson(raw) : String(raw);
        },
        // 秒 → 1天2小时3分4秒（不足 1 秒保留小数；毫秒换算后 <10 秒保留 1 位小数）
        formatDuration(seconds, fractional) {
            const neg = seconds < 0;
            const abs = Math.abs(seconds);
            if (abs < 1) {
                return (neg ? '-' : '') + String(Number(abs.toFixed(2))) + '秒';
            }
            if (fractional && abs < 10) {
                return (neg ? '-' : '') + String(Number(abs.toFixed(1))) + '秒';
            }
            let rest = Math.round(abs);
            const days = Math.floor(rest / 86400);
            rest -= days * 86400;
            const hours = Math.floor(rest / 3600);
            rest -= hours * 3600;
            const minutes = Math.floor(rest / 60);
            const secs = rest - minutes * 60;
            let text = '';
            if (days) text += days + '天';
            if (hours) text += hours + '小时';
            if (minutes) text += minutes + '分';
            if (secs || !text) text += secs + '秒';
            return (neg ? '-' : '') + text;
        },
        // 字节 → 1.5 KB / 23 MB / 1.2 GB（1024 进制；B 不带小数，其余按大小保留 0~2 位）
        formatBytes(bytes) {
            const neg = bytes < 0;
            const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
            let value = Math.abs(bytes);
            let index = 0;
            while (value >= 1024 && index < units.length - 1) {
                value /= 1024;
                index += 1;
            }
            const digits = index === 0 ? 0 : (value >= 100 ? 0 : (value >= 10 ? 1 : 2));
            const text = String(Number(value.toFixed(digits)));
            return (neg ? '-' : '') + text + ' ' + units[index];
        },
        // 千分位：每三位整数加一个英文逗号，小数位原样保留（1234.50 → 1,234.50）
        formatThousands(raw) {
            const text = String(raw === null || raw === undefined ? '' : raw).trim();
            // 科学计数法（1e+21）等特殊写法不做处理，避免逗号加错位置
            if (!/^-?\d+(\.\d+)?$/.test(text)) return text;
            const sign = text.charAt(0) === '-' ? '-' : '';
            const body = sign ? text.slice(1) : text;
            const dot = body.indexOf('.');
            const intPart = dot < 0 ? body : body.slice(0, dot);
            const decPart = dot < 0 ? '' : body.slice(dot);
            return sign + this.groupDigits(intPart) + decPart;
        },
        // 整数部分每三位加一个英文逗号
        groupDigits(intPart) {
            return String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        // 金额：整数加千分位、固定 2 位小数（1234.5 → 1,234.50）
        formatMoney(num) {
            const neg = num < 0;
            const parts = Math.abs(num).toFixed(2).split('.');
            return (neg ? '-' : '') + this.groupDigits(parts[0]) + '.' + parts[1];
        },
        // 百分比：0.1234 → 12.34%（最多 2 位小数，去掉尾随 0）
        formatPercent(num) {
            return String(Number((num * 100).toFixed(2))) + '%';
        },
        // 时间戳 → yyyy-MM-dd HH:mm:ss；按量级自动识别 秒 / 毫秒 / 微秒
        formatTimestamp(num) {
            const abs = Math.abs(num);
            let ms = num;
            if (abs >= 1e14) ms = num / 1000;      // 微秒
            else if (abs >= 1e11) ms = num;        // 毫秒
            else ms = num * 1000;                  // 秒
            const date = new Date(ms);
            if (isNaN(date.getTime())) return String(num);
            const pad = n => String(n).padStart(2, '0');
            return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) +
                ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds());
        },
        // 布尔：0/1、true/false、t/f、y/n → 是 / 否（识别不了的原样返回）
        formatBool(raw) {
            const v = String(raw === null || raw === undefined ? '' : raw).trim().toLowerCase();
            if (BOOL_TRUE.indexOf(v) >= 0) return '是';
            if (BOOL_FALSE.indexOf(v) >= 0) return '否';
            return raw;
        },
        // JSON 单行压缩（单元格内展示；原始文本本来就是多行时也能收成一行）
        compactJson(raw) {
            try {
                return JSON.stringify(JSON.parse(String(raw)));
            } catch (e) {
                return raw;
            }
        },
        // JSON 格式化（悬浮 title 里看结构）
        formatJson(raw) {
            try {
                return JSON.stringify(JSON.parse(String(raw)), null, 2);
            } catch (e) {
                return String(raw);
            }
        },
        // 设置某列的展示格式（传空表示取消）
        setColumnFormat(name, value) {
            const next = Object.assign({}, this.formatByColumn);
            if (value) next[name] = value;
            else delete next[name];
            this.formatByColumn = next;
            // 格式会改变展示文本长度 → 重新量算内容宽度
            this.rebuildContentWidths();
        },
        emitFavorite() {
            if (this.selection.length === 0 || this.selection.length > this.maxSelect) return;
            const columns = this.result.columns || [];
            const rows = this.selection.map(item => columns.map((col, ci) => item['c' + ci]));
            this.$emit('favorite', rows);
        },
        // ---------- 结果表格编辑（简单单表查询，见 [11 篇]） ----------
        // 重建编辑副本（进入编辑 / 回滚 / 结果刷新时调用）
        resetEditRows() {
            this.deletedRows = [];
            this.selection = [];
            this.editActive = false;
            this.editSeq = 0;
            if (!this.canEdit) {
                this.editRows = [];
                this.rebuildColumnFilters();
                this.rebuildContentWidths();
                return;
            }
            const columns = this.result.columns || [];
            this.editRows = (this.result.rows || []).map((row) => {
                const obj = { __idx: this.editSeq++, __new: false };
                const orig = [];
                columns.forEach((col, ci) => {
                    const v = row[ci] === undefined ? null : row[ci];
                    obj['c' + ci] = v;
                    orig.push(v);
                });
                obj.__orig = orig;
                return obj;
            });
            this.rebuildColumnFilters();
            this.rebuildContentWidths();
        },
        // 单元格值归一化（用于比较是否改动）
        normCell(v) {
            return v === null || v === undefined ? '' : String(v);
        },
        // 原始列名在结果列中的下标（按 col.column 匹配）
        columnIndexOf(columnName) {
            const columns = this.result.columns || [];
            const want = String(columnName).toLowerCase();
            for (let ci = 0; ci < columns.length; ci++) {
                const c = columns[ci];
                if (c.column && String(c.column).toLowerCase() === want) return ci;
            }
            return -1;
        },
        // 该行对应的主键键值对象（{ 原始列名: 值 }）
        rowKeys(row) {
            const keys = {};
            (this.editMeta && this.editMeta.pkColumns ? this.editMeta.pkColumns : []).forEach(pk => {
                const ci = this.columnIndexOf(pk);
                if (ci >= 0) keys[pk] = row['c' + ci];
            });
            return keys;
        },
        // 该列是否需要编辑能力（可编辑列，或主键列——主键列仅「新增行」可填，
        // 是否允许由 beforeEditMethod 按行判定；列级先开启 edit-render）
        columnEditRender(col) {
            if (!this.canEdit || !col || !col.column) return null;
            const name = String(col.column).toLowerCase();
            if (this.editableColumnSet.has(name) || this.pkColumnSet.has(name)) {
                // immediate：输入即时写回行数据（否则值只留在编辑器 model 里，提交时取不到）
                return { name: 'input', immediate: true };
            }
            return null;
        },
        // 单元格是否可编辑：可编辑列可编辑；新增行的主键列也可填写
        isCellEditable(row, ci) {
            if (!this.canEdit) return false;
            const col = (this.result.columns || [])[ci];
            if (!col || !col.column) return false;
            const name = String(col.column).toLowerCase();
            if (this.editableColumnSet.has(name)) return true;
            return !!(row && row.__new && this.pkColumnSet.has(name));
        },
        // vxe beforeEditMethod：按列 field（'c{索引}'）判断该单元格能否编辑
        canEditCellField(row, field) {
            if (!this.canEdit || !field || field.charAt(0) !== 'c') return false;
            const ci = Number(field.slice(1));
            return Number.isInteger(ci) && this.isCellEditable(row, ci);
        },
        // 单元格进入编辑（双击出现输入框）→ 提交/回滚点亮
        onEditActived() {
            this.editActive = true;
        },
        // 单元格退出编辑
        onEditClosed() {
            this.editActive = false;
        },
        // 单元格是否已被修改（用于高亮）
        isCellDirty(row, ci) {
            if (!row || row.__new) return false;
            const col = (this.result.columns || [])[ci];
            if (!col || !col.column) return false;
            if (!this.editableColumnSet.has(String(col.column).toLowerCase())) return false;
            return this.normCell(row['c' + ci]) !== this.normCell(row.__orig ? row.__orig[ci] : null);
        },
        // 新增行整行高亮
        rowClassName({ row }) {
            return row && row.__new ? 'dbresult__row--new' : '';
        },
        // 新增一行（空白行，提交时生成 INSERT）
        addRow() {
            if (!this.canEdit) return;
            const columns = this.result.columns || [];
            const row = { __idx: this.editSeq++, __new: true, __orig: null };
            columns.forEach((col, ci) => {
                row['c' + ci] = '';
            });
            // 第一个可编辑列（用于自动进入编辑）
            let firstCi = -1;
            for (let ci = 0; ci < columns.length; ci++) {
                if (this.isCellEditable(row, ci)) {
                    firstCi = ci;
                    break;
                }
            }
            // 赋新数组（而非 push）：vxe-table 依据 data 引用变化触发更新
            this.editRows = this.editRows.concat(row);
            this.$nextTick(() => {
                const table = this.$refs.xTable;
                const target = this.editRows[this.editRows.length - 1];
                if (table && table.scrollToRow && target) table.scrollToRow(target);
                // 自动进入第一个可编辑单元格
                if (table && table.setEditCell && target && firstCi >= 0) {
                    table.setEditCell(target, 'c' + firstCi);
                }
            });
        },
        // 删除勾选的行（未提交前只标记，可回滚）
        deleteSelectedRows() {
            if (!this.selection.length) {
                ElMessage({ message: '请先勾选要删除的行', type: 'warning', duration: 2000 });
                return;
            }
            const selected = this.selection.slice();
            const keep = [];
            this.editRows.forEach(row => {
                if (selected.indexOf(row) >= 0) {
                    // 原有行 → 记录为待删除（生成 DELETE）；新增行 → 直接丢弃
                    if (!row.__new) this.deletedRows.push(row);
                } else {
                    keep.push(row);
                }
            });
            this.editRows = keep;
            this.clearSelection();
        },
        // 回滚：丢弃全部本地改动
        rollbackChanges() {
            this.resetEditRows();
        },
        // 汇总本地改动为后端 changes 结构
        collectChanges() {
            if (!this.canEdit) return [];
            const columns = this.result.columns || [];
            const editableSet = this.editableColumnSet;
            const pkSet = this.pkColumnSet;
            const changes = [];
            // 新增
            this.editRows.forEach(row => {
                if (!row.__new) return;
                const values = {};
                columns.forEach((col, ci) => {
                    if (!col.column) return;
                    const name = String(col.column).toLowerCase();
                    if (!editableSet.has(name) && !pkSet.has(name)) return;
                    const v = row['c' + ci];
                    if (v === null || v === undefined || String(v) === '') return; // 空则交给库默认值
                    values[col.column] = v;
                });
                if (Object.keys(values).length) changes.push({ type: 'insert', values });
            });
            // 修改（只提交被改动的可编辑列）
            this.editRows.forEach(row => {
                if (row.__new) return;
                const values = {};
                columns.forEach((col, ci) => {
                    if (!col.column) return;
                    if (!editableSet.has(String(col.column).toLowerCase())) return;
                    const now = this.normCell(row['c' + ci]);
                    const old = this.normCell(row.__orig ? row.__orig[ci] : null);
                    if (now !== old) values[col.column] = row['c' + ci];
                });
                if (Object.keys(values).length) {
                    changes.push({ type: 'update', keys: this.rowKeys(row), values });
                }
            });
            // 删除
            this.deletedRows.forEach(row => {
                changes.push({ type: 'delete', keys: this.rowKeys(row) });
            });
            return changes;
        },
        // 提交：交给父组件（生产防护锁确认 + 调接口 + 成功后重查刷新）
        async submitChanges() {
            if (!this.canEdit || !this.commitHandler) return;
            const changes = this.collectChanges();
            if (!changes.length) {
                ElMessage({ message: '没有需要提交的改动', type: 'warning', duration: 2000 });
                return;
            }
            const edit = this.editMeta || {};
            this.savingEdit = true;
            try {
                await this.commitHandler({
                    table: edit.table,
                    schema: edit.schema || '',
                    database: edit.database || '',
                    changes
                });
            } finally {
                this.savingEdit = false;
            }
        },
        // 展示用的字段类型：优先用结果自带类型；为空时回退到表字段类型
        colType(col) {
            if (col && col.type) return col.type;
            const meta = this.matchColumnMeta(col);
            return (meta && meta.type) || '';
        },
        // 字段注释：按结果列的来源表 + 原始列名去列缓存里匹配（后端已处理别名）
        colComment(col) {
            const meta = this.matchColumnMeta(col);
            return (meta && meta.comment) || '';
        },
        // 依据结果列自带的 table/column 从列缓存中取出对应字段元数据
        matchColumnMeta(col) {
            const cache = this.columnMeta;
            if (!cache || !col || !col.table || !col.column) return null;
            const key = this.resolveTableKey(col.table);
            if (!key) return null;
            const cols = cache[key] || [];
            const want = String(col.column).toLowerCase();
            return cols.find(c => c && String(c.name).toLowerCase() === want) || null;
        },
        // 列缓存的键可能是「schema.table」或裸表名，这里做「全名精确 → 短名」两步匹配
        resolveTableKey(name) {
            const cache = this.columnMeta || {};
            const want = String(name).toLowerCase();
            const keys = Object.keys(cache);
            let key = keys.find(k => k.toLowerCase() === want);
            if (key) return key;
            key = keys.find(k => k.split('.').pop().toLowerCase() === want);
            return key || '';
        },
        // 文本宽度估算：中日韩/全角按 2 个单位，其余按 1 个单位
        textWidth(text, unit) {
            let units = 0;
            for (const ch of String(text === null || text === undefined ? '' : text)) {
                units += ch.codePointAt(0) > 0x2e7f ? 2 : 1;
            }
            return units * unit;
        },
        // 表头底线宽度：三行（名称/类型/注释）中最宽的一行 + 右侧图标占位
        // （名称/类型/注释 + 排序按钮 + 筛选按钮必须完整显示，不能被挤压）
        headerMinWidth(col) {
            const name = this.textWidth(col && col.name, 7.6);
            const type = this.textWidth(this.colType(col), 6.6);
            // 注释属于补充信息，过长不无限撑宽（超长省略 + 悬浮看全文）
            const comment = Math.min(this.textWidth(this.colComment(col), 6.6), 280);
            return Math.ceil(Math.max(name, type, comment)) + HEADER_RESERVE;
        },
        // 列宽：表头底线 → 内容宽度参与扩宽 → 上限封顶；同时不小于最小宽度
        columnWidth(col, field) {
            const headerMin = this.headerMinWidth(col);
            const content = this.contentWidths[field] || 0;
            return Math.max(MIN_COL_WIDTH, headerMin, Math.min(content, MAX_COL_WIDTH));
        },
        // 展开/收起用「元素实际高度」驱动 max-height 过渡（固定大值会导致末尾突缩，即「抽一下」）
        onPanelEnter(el) {
            el.style.overflow = 'hidden';
            el.style.maxHeight = '0px';
            void el.offsetHeight; // 强制一次布局，让 0 生效
            el.style.maxHeight = el.scrollHeight + 'px';
        },
        onPanelAfterEnter(el) {
            // 动画结束清除内联高度，避免限制后续内容变化
            el.style.maxHeight = '';
            el.style.overflow = '';
        },
        onPanelLeave(el) {
            el.style.overflow = 'hidden';
            el.style.maxHeight = el.scrollHeight + 'px';
            void el.offsetHeight;
            el.style.maxHeight = '0px';
        },
        onPanelAfterLeave(el) {
            el.style.maxHeight = '';
            el.style.overflow = '';
        }
    }
};
</script>

<!-- 抽屉与弹窗会挂到 body 下，样式放非 scoped 块并统一前缀 -->
<style>
.dbresult {
    /* 执行中的加载遮罩以此为定位父级 */
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.dbresult__body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
}

.dbresult__tablewrap {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
}

.dbresult__tablebox {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

/* 执行中：在保留的旧结果上叠加半透明遮罩 + 转圈 */
.dbresult__loading {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.6);
}

.dbresult__loading .el-icon {
    color: #409eff;
}

/* 遮罩的淡入淡出 */
.dbresult-fade-enter-active,
.dbresult-fade-leave-active {
    transition: opacity 0.2s ease;
}

.dbresult-fade-enter-from,
.dbresult-fade-leave-to {
    opacity: 0;
}

.dbresult__error {
    padding: 8px 12px;
    border-radius: 8px;
    background-color: #fef0f0;
    color: #f56c6c;
    font-size: 13px;
}

.dbresult__status {
    display: flex;
    flex: 0 0 auto;
    align-items: baseline;
    font-size: 13px;
    color: #5b6a80;
}

/* 本次执行的 SQL：紧跟状态行，超长省略号截断，悬浮 title 查看完整语句 */
.dbresult__sql {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #7d8ba1;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

.dbresult__sep {
    margin: 0 8px;
    color: #c0c4cc;
}

.dbresult__warn {
    margin-top: 8px;
}

.dbresult__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 12px;
    margin: 10px 0 6px;
}

.dbresult__tips {
    font-size: 12px;
    color: #aab4c4;
}

/* 结果表格编辑：右上角操作条 */
.dbresult__editbar {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.dbresult__editbar .el-button {
    margin-left: 0;
}

.dbresult__editcount {
    font-size: 12px;
    color: #e6a23c;
}

.dbresult__editlock {
    flex: none;
    margin-left: auto;
    font-size: 12px;
    color: #aab4c4;
    cursor: help;
}

/* 可编辑单元格：默认纯文本（双击由 vxe 进入编辑），改动高亮 */
.dbresult__cell-view {
    display: block;
    min-height: 18px;
    padding: 0 4px;
    box-sizing: border-box;
    cursor: text;
}

.dbresult__cell-view.is-dirty {
    background-color: #fff8e6;
    box-shadow: 0 0 0 1px #f0c36d inset;
}

/* vxe 内置 input 渲染器（编辑态）铺满单元格 */
.dbresult__table .vxe-default-input {
    width: 100%;
    height: 100%;
    padding: 0 4px;
    box-sizing: border-box;
    border: 1px solid #409eff;
    border-radius: 3px;
    outline: none;
    font: inherit;
    color: inherit;
    background-color: #fff;
}

/* 新增行整行高亮 */
.dbresult__table .dbresult__row--new .vxe-body--column {
    background-color: #f0f9eb;
}

/* 「格式」面板（el-popover 挂 body，样式放非 scoped） */
.dbresult-format {
    max-height: 320px;
    overflow-y: auto;
}

.dbresult-format__head {
    margin-bottom: 8px;
    color: #8a94a6;
    font-size: 12px;
    line-height: 1.5;
}

.dbresult-format__empty {
    padding: 6px 0;
    color: #aab4c4;
    font-size: 12px;
}

.dbresult-format__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 3px 0;
}

.dbresult-format__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2d3d;
    font-size: 13px;
}

/* 原生 select：不用 el-select 是因为其下拉会挂到 body，点选项会被 el-popover 判为「点外面」而关闭面板 */
.dbresult-format__select {
    flex: 0 0 auto;
    width: 176px;
    height: 26px;
    padding: 0 4px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
    color: #1f2d3d;
    font-size: 12px;
    line-height: 24px;
    outline: none;
    cursor: pointer;
}

.dbresult-format__select:hover {
    border-color: #c0c4cc;
}

.dbresult-format__select:focus {
    border-color: #409eff;
}

.dbresult__toolbar {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 10px;
    margin: 8px 0 6px;
}

.dbresult__search {
    width: 260px;
}

.dbresult__match {
    font-size: 12px;
    color: #7d8ba1;
}

.dbresult__null {
    color: #aab4c4;
    font-style: italic;
}

/* 表头单元格改为 flex：标题占满剩余宽度、排序图标固定在同行右侧，
   避免自定义多行表头（块级）把排序图标挤到新的一行（浪费纵向空间） */
.dbresult__table .vxe-header--column > .vxe-cell {
    display: flex;
    align-items: center;
}

.dbresult__table .vxe-header--column > .vxe-cell > .vxe-cell--title {
    flex: 1 1 auto;
    min-width: 0;
}

.dbresult__table .vxe-header--column > .vxe-cell > .vxe-cell--sort {
    flex: 0 0 auto;
}

/* 列头漏斗筛选图标：与排序图标一样固定在标题同一行右侧 */
.dbresult__table .vxe-header--column > .vxe-cell > .vxe-cell--filter {
    flex: 0 0 auto;
}

/* 列设置面板（vxe simple 模式）默认贴表格右上角，这里改到左上角 */
.dbresult__table .vxe-table-custom-wrapper {
    top: -4px;
    right: auto;
    left: 2px;
}

/* 结果列表头：三行（字段名 / 字段类型 / 字段注释），超长省略 */
.dbresult__head {
    display: flex;
    flex-direction: column;
    gap: 1px;
    overflow: hidden;
    line-height: 1.35;
}

.dbresult__head-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #1f2d3d;
    font-weight: 600;
}

.dbresult__head-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #8a94a6;
    font-size: 11px;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

.dbresult__head-cmt {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #aab4c4;
    font-size: 11px;
    font-weight: 400;
}

/* 结果出现/消失：高度过渡由 JS 按「元素实际高度」驱动（见 onPanelEnter/onPanelLeave），
   这里只定义过渡曲线与淡入淡出，避免固定 max-height 造成末尾突缩（「抽一下」）。 */
.dbresult-anim-enter-active,
.dbresult-anim-leave-active {
    overflow: hidden;
    transition: max-height 0.4s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.3s ease,
        transform 0.3s ease;
}

.dbresult-anim-enter-from,
.dbresult-anim-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
